import React from 'react';
import { TrendingUp, Building, User, DollarSign, FileText } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';
import Select from 'components/ui/Select';
import { Checkbox } from 'components/ui/Checkbox';

const AccountPreferencesStep = ({ formData, onInputChange, error }) => {
  const { t } = useLanguage();

  const tradingExperienceOptions = [
    { value: '', label: t('Select your experience level') },
    { value: 'beginner', label: t('Beginner (0-1 years)') },
    { value: 'intermediate', label: t('Intermediate (1-3 years)') },
    { value: 'advanced', label: t('Advanced (3-5 years)') },
    { value: 'expert', label: t('Expert (5+ years)') }
  ];

  const accountTypeOptions = [
    { value: 'individual', label: t('Individual Account') },
    { value: 'corporate', label: t('Corporate Account') }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('Account Preferences')}
        </h2>
        <p className="text-gray-600">
          {t('Configure your trading account settings and preferences')}
        </p>
      </div>
      {/* Trading Experience */}
      <div className="space-y-2">
        <label htmlFor="tradingExperience" className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            {t('Trading Experience Level')} *
          </div>
        </label>
        <Select
          id="tradingExperience"
          value={formData?.tradingExperience}
          onChange={(e) => onInputChange('tradingExperience', e?.target?.value)}
          required
        >
          {tradingExperienceOptions?.map(option => (
            <option key={option?.value} value={option?.value}>
              {option?.label}
            </option>
          ))}
        </Select>
        <p className="text-xs text-gray-500">
          {t('This helps us provide appropriate risk management tools')}
        </p>
      </div>
      {/* Account Type */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            {t('Account Type')} *
          </div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accountTypeOptions?.map(option => (
            <div
              key={option?.value}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                formData?.accountType === option?.value
                  ? 'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => onInputChange('accountType', option?.value)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  formData?.accountType === option?.value
                    ? 'border-blue-500 bg-blue-500' :'border-gray-300'
                }`}>
                  {formData?.accountType === option?.value && (
                    <div className="w-2 h-2 bg-white rounded-full m-auto mt-0.5"></div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {option?.value === 'individual' ? (
                    <User className="w-5 h-5 text-gray-600" />
                  ) : (
                    <Building className="w-5 h-5 text-gray-600" />
                  )}
                  <span className="font-medium">{option?.label}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {option?.value === 'individual' ? t('For personal trading activities')
                  : t('For business and corporate trading')
                }
              </p>
            </div>
          ))}
        </div>
      </div>
      {/* Base Currency */}
      <div className="space-y-2">
        <label htmlFor="baseCurrency" className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            {t('Base Currency')} *
          </div>
        </label>
        <Select
          id="baseCurrency"
          value={formData?.baseCurrency}
          onChange={(e) => onInputChange('baseCurrency', e?.target?.value)}
          required
        >
          {currencyOptions?.map(option => (
            <option key={option?.value} value={option?.value}>
              {option?.label}
            </option>
          ))}
        </Select>
        <p className="text-xs text-gray-500">
          {t('This will be used for account balance and reporting')}
        </p>
      </div>
      {/* Risk Disclosure */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800 mb-2">
              {t('Risk Disclosure')}
            </h3>
            <p className="text-sm text-amber-700 mb-3">
              {t('Trading involves substantial risk and may not be suitable for all investors. Past performance does not guarantee future results. Please ensure you fully understand the risks involved before trading.')}
            </p>
            <details className="text-sm">
              <summary className="cursor-pointer text-amber-800 font-medium hover:text-amber-900">
                {t('View Full Risk Disclosure')}
              </summary>
              <div className="mt-2 text-amber-700 space-y-2">
                <p>{t('• High degree of leverage can work against you as well as for you')}</p>
                <p>{t('• You may sustain a total loss of initial margin funds')}</p>
                <p>{t('• Currency trading is not suitable for all investors')}</p>
                <p>{t('• Seek independent advice if necessary')}</p>
              </div>
            </details>
          </div>
        </div>
      </div>
      {/* Legal Agreements */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">
          {t('Legal Agreements')} *
        </h3>

        {/* Terms of Service */}
        <div className="flex items-start gap-3">
          <Checkbox
            id="agreedToTerms"
            checked={formData?.agreedToTerms}
            onChange={(e) => onInputChange('agreedToTerms', e?.target?.checked)}
            className="mt-1"
          />
          <label htmlFor="agreedToTerms" className="text-sm text-gray-700 flex-1">
            {t('I agree to the')}{' '}
            <a
              href="/terms-of-service"
              target="_blank"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {t('Terms of Service')}
            </a>{' '}
            {t('and')}{' '}
            <a
              href="/user-agreement"
              target="_blank"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {t('User Agreement')}
            </a>
          </label>
        </div>

        {/* Privacy Policy */}
        <div className="flex items-start gap-3">
          <Checkbox
            id="agreedToPrivacy"
            checked={formData?.agreedToPrivacy}
            onChange={(e) => onInputChange('agreedToPrivacy', e?.target?.checked)}
            className="mt-1"
          />
          <label htmlFor="agreedToPrivacy" className="text-sm text-gray-700 flex-1">
            {t('I agree to the')}{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {t('Privacy Policy')}
            </a>{' '}
            {t('and consent to the processing of my personal data')}
          </label>
        </div>
      </div>
    </div>
  );
};

export default AccountPreferencesStep;